import { useReactiveVar } from '@apollo/client'
import { unstable_batchedUpdates } from 'react-dom'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { FamilyIteration, ProductFamilies } from 'models/projectElementNode'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { getAssemblyLevel } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import { useCallback, useContext, useMemo } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { openCenteredBrowserWindow } from 'shared/utils/browserUtils'
import {
    getPrintPreviewUrl,
    getSortedList,
    WorkspacePrintPreview,
} from 'shared/utils/workspaceUtils'
import {
    useActionBarActions,
    useElementList,
    useSelectedOMVersion,
} from '../ActionBarStore'
import { useExportSectionMutation } from '../api/mutations/exportSection'
import { useSupportingDocumentsQuery } from '../api/supportingDocs'
import { useNotification } from '../services/notificationService'
import { FileType, NodeType } from '../types'
import { useShallowSelector } from '../Workspace2Store'
import { useContentItemFetcher } from './useContentItemFetcher'
import { usePermissionChecks } from './usePermissionChecks'
import { useTreeDataMapping } from './useTreeDataMapping'
import { useTreeExpansion } from './useTreeExpansion'

// Constants
const BROWSER_WINDOW_CONFIG = {
    features:
        'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes',
    windowNamePrefix: 'Preview',
} as const

const SNACKBAR_CONFIG = {
    messages: {
        versionAlreadyLoaded: 'Selected version is already loaded',
        versionSwitchSuccess: 'Switched Version Successfully',
        versionSwitchError: 'Failed to switch version',
    },
    ids: {
        versionUpdateError: 'VersionSwitch_UpdateError',
        versionUpdateSuccess: 'VersionSwitch_UpdateSuccess',
    },
} as const

const ASSEMBLY_CONFIG = {
    divisionLevelLength: 2,
    firstLevel: 1,
} as const

// Interfaces
interface AssemblyMetrics {
    assemblyText: string
    assemblyLevel: number
    isDivisionLevel: boolean
}

interface TreeExpansionHandlers {
    handleExpandAll: () => void
    handleCollapseAll: () => void
    keysToExpand: string[]
}

interface ExportHandlers {
    handleExportWord: () => void
    handleExportPDF: () => void
    handleViewPrintPreview: () => void
}

interface SupportingDocsHandler {
    handleViewSupportingDocs: () => void
}

interface VersionManagementHandler {
    handleOMVersionSelectChange: (
        value: ProductFamilies[] | FamilyIteration
    ) => void
}

interface SelectedElementNode {
    elementId?: string
    baseElementId?: string
    id?: string
    text?: string
    elementType?: string
    treePath?: string
    parentTreePath?: string
}

// Custom hook for assembly calculations
const useAssemblyMetrics = (
    selectedProjectElementNode: SelectedElementNode | null
): AssemblyMetrics => {
    return useMemo(() => {
        if (!selectedProjectElementNode?.text) {
            return {
                assemblyText: '',
                assemblyLevel: 0,
                isDivisionLevel: false,
            }
        }

        const assemblyText = selectedProjectElementNode.text
        const assemblyTextParts = assemblyText.split('|')[0]
        const assemblyLevel = getAssemblyLevel(assemblyTextParts)
        const isDivisionLevel =
            assemblyTextParts?.length === ASSEMBLY_CONFIG.divisionLevelLength

        return {
            assemblyText,
            assemblyLevel,
            isDivisionLevel,
        }
    }, [selectedProjectElementNode?.text])
}

// Custom hook for export functionality
const useExportHandlers = (
    selectedProjectElementNode: SelectedElementNode | null,
    isStudioManagement: boolean,
    projectId: string,
    projectProductFamilyId: string,
    setIsExportLoading: any,
    exportSection: any
): ExportHandlers => {
    const getProjectProductFamilyId = useCallback(() => {
        if (isStudioManagement) {
            return (
                selectedProjectElementNode?.elementId ||
                selectedProjectElementNode?.baseElementId
            )
        }
        return (
            projectProductFamilyId ||
            selectedProjectElementNode?.elementId ||
            selectedProjectElementNode?.baseElementId
        )
    }, [isStudioManagement, selectedProjectElementNode, projectProductFamilyId])

    const handleExport = useCallback(
        async (fileType: FileType) => {
            try {
                const hasElementId = Boolean(
                    selectedProjectElementNode?.elementId
                )
                const hasBaseElementId =
                    isStudioManagement &&
                    Boolean(selectedProjectElementNode?.baseElementId)

                if (!hasElementId && !hasBaseElementId) {
                    console.warn(
                        'No element ID or base element ID available for export'
                    )
                    return
                }

                if (!fileType) {
                    console.warn('No file type specified for export')
                    return
                }

                setIsExportLoading(fileType, true)

                await exportSection({
                    variables: {
                        exportSectionInput: {
                            projectId,
                            projectProductFamilyId: getProjectProductFamilyId(),
                            nodeId: selectedProjectElementNode?.id,
                            familyTreePath:
                                selectedProjectElementNode?.elementType ===
                                ProjectElementType.PRODUCTFAMILY
                                    ? selectedProjectElementNode?.treePath
                                    : selectedProjectElementNode?.parentTreePath,
                            fileType,
                        },
                    },
                })
            } catch (error) {
                console.error(`Error exporting ${fileType}:`, error)
                setIsExportLoading(fileType, false)
            }
        },
        [
            selectedProjectElementNode,
            isStudioManagement,
            projectId,
            getProjectProductFamilyId,
            setIsExportLoading,
            exportSection,
        ]
    )

    const handleExportWord = useCallback(() => handleExport(FileType.DOCX), [
        handleExport,
    ])
    const handleExportPDF = useCallback(() => handleExport(FileType.PDF), [
        handleExport,
    ])

    const { previewFeatures } = usePreviewFeatures()

    const handleViewPrintPreview = useCallback(() => {
        try {
            const hasElementId = Boolean(selectedProjectElementNode?.elementId)

            if (!hasElementId && !isStudioManagement) {
                console.warn('No element ID available for print preview')
                return
            }

            const previewUrl = getPrintPreviewUrl({
                projectId,
                elementNode: selectedProjectElementNode,
                projectProductFamilyId,
                isStudioManagement,
                isOMPTAutomation: previewFeatures.hasOMPTAutomation,
            } as WorkspacePrintPreview)

            if (!previewUrl) {
                console.warn('Failed to generate print preview URL')
                return
            }

            openCenteredBrowserWindow(
                previewUrl,
                `${BROWSER_WINDOW_CONFIG.windowNamePrefix}${projectId}`,
                BROWSER_WINDOW_CONFIG.features
            )
        } catch (error) {
            console.error('Error opening print preview:', error)
        }
    }, [
        projectId,
        selectedProjectElementNode,
        projectProductFamilyId,
        isStudioManagement,
        previewFeatures.hasOMPTAutomation,
    ])

    return { handleExportWord, handleExportPDF, handleViewPrintPreview }
}

// Custom hook for supporting documents
const useSupportingDocs = (
    projectProductFamilyId: string,
    getSupportingDocuments: any
): SupportingDocsHandler => {
    const handleViewSupportingDocs = useCallback(() => {
        try {
            if (!projectProductFamilyId) {
                console.warn(
                    'No project product family ID available for supporting documents'
                )
                return
            }

            getSupportingDocuments({
                variables: {
                    getSupportingDocsInput: {
                        productFamilyId: projectProductFamilyId,
                    },
                },
            })
        } catch (error) {
            console.error('Error fetching supporting documents:', error)
        }
    }, [projectProductFamilyId, getSupportingDocuments])

    return { handleViewSupportingDocs }
}

// Custom hook for version management
export const useVersionManagement = (): VersionManagementHandler => {
    const {
        categoryElementStates,
        setCategoryElementStates,
        setSelectedElementIdentifier,
    } = useContext(ProjectPageContext)

    const notification = useNotification()
    const selectedOMVersion = useSelectedOMVersion()
    const elementList = useElementList()
    const { setSelectedOMVersion, setElementList } = useActionBarActions()

    const handleOMVersionSelectChange = useCallback(
        (value: ProductFamilies[] | FamilyIteration) => {
            try {
                const currentValue = value as FamilyIteration

                if (
                    selectedOMVersion?.iterationName ===
                    currentValue?.iterationName
                ) {
                    notification.error({
                        message: SNACKBAR_CONFIG.messages.versionAlreadyLoaded,
                    })
                    return
                }

                if (!currentValue?.elementNodes || !elementList) {
                    notification.error({
                        message: SNACKBAR_CONFIG.messages.versionSwitchError,
                    })
                    return
                }

                const selectedElementNodes = getSortedList(
                    currentValue.elementNodes,
                    'nodeOrder'
                )

                if (!selectedElementNodes.length) {
                    notification.error({
                        message: SNACKBAR_CONFIG.messages.versionSwitchError,
                    })
                    return
                }

                // Use the CURRENT version's parentTreePath to locate nodes in
                // the element list — different iterations have different tree
                // paths so the new version's parentTreePath won't match.
                // Falls back to the new version's path on initial load when no
                // prior version nodes are available yet.
                const currentVersionNodes = getSortedList(
                    selectedOMVersion?.elementNodes ?? [],
                    'nodeOrder'
                )
                const lookupParentTreePath =
                    currentVersionNodes[0]?.parentTreePath ??
                    selectedElementNodes[0]?.parentTreePath

                if (!lookupParentTreePath) {
                    notification.error({
                        message: SNACKBAR_CONFIG.messages.versionSwitchError,
                    })
                    return
                }

                // Try to find the parent node; fall back to the first child if
                // the section header isn't in the list (initial load omits it)
                const parentNodeIndex = elementList.findIndex(
                    (item) => item?.treePath === lookupParentTreePath
                )
                const firstChildIndex =
                    parentNodeIndex === -1
                        ? elementList.findIndex(
                              (item) =>
                                  item?.parentTreePath === lookupParentTreePath
                          )
                        : -1
                const replacementIndex =
                    parentNodeIndex !== -1 ? parentNodeIndex : firstChildIndex

                if (replacementIndex === -1) {
                    notification.error({
                        message: SNACKBAR_CONFIG.messages.versionSwitchError,
                    })
                    return
                }

                setSelectedOMVersion(currentValue)

                // When parent found: keep parent, replace children after it.
                // When only child found: replace from the first child onwards.
                const sliceUpTo =
                    parentNodeIndex !== -1
                        ? replacementIndex + 1
                        : replacementIndex

                const updatedElementList = [
                    ...elementList.slice(0, sliceUpTo),
                    ...selectedElementNodes,
                ]

                // In React 17, automatic batching only applies within React-managed
                // event handlers. setElementList writes to a Zustand store and
                // setSelectedElementIdentifier dispatches a React useState setter
                // (from ProjectPageContext), so React 17 does not batch them
                // automatically — each triggers its own render without this wrapper.
                // Without batching, setSelectedElementIdentifier could fire in a
                // separate render from setElementList, making the element appear
                // selected before it exists in the tree, which prevents
                // useElementToTreeMapper from triggering and leaves the content
                // fetch in a stuck state.
                //
                // Do NOT call setIsTreeLoading(true) here: when isTreeLoading is
                // true, TreeView renders only LoadingSkeletons and unmounts all
                // ElementItemCard components. useElementToTreeMapper lives inside
                // ElementItemCard — if the card is unmounted it cannot observe
                // isSelected becoming true, so the content fetch never starts and
                // the tree stays stuck in a loading state indefinitely.
                unstable_batchedUpdates(() => {
                    setElementList(updatedElementList)
                    setCategoryElementStates((prev) => ({
                        ...prev,
                        all: { ...prev.all, data: updatedElementList },
                    }))

                    const { baseElementId, id, treePath } = selectedElementNodes[0]
                    setSelectedElementIdentifier({
                        baseElementId,
                        id,
                        treePath,
                    })
                })

                notification.success({
                    message: SNACKBAR_CONFIG.messages.versionSwitchSuccess,
                })
            } catch (error) {
                console.error('Error handling OM version change:', error)
                notification.error({
                    message: SNACKBAR_CONFIG.messages.versionSwitchError,
                })
            }
        },
        [
            selectedOMVersion,
            elementList,
            setCategoryElementStates,
            setSelectedElementIdentifier,
            setElementList,
            setSelectedOMVersion,
            notification,
        ]
    )

    return { handleOMVersionSelectChange }
}

// Main hook
export const useActionBarHandler = (
    projectId: string,
    projectProductFamilyId: string
) => {
    const {
        selectedElementNode: rawSelectedProjectElementNode,
    } = useReactiveVar(workspaceSelectedElement)

    // Map ProjectElementNode (with possible nulls) to SelectedElementNode (with undefined)
    const selectedProjectElementNode: SelectedElementNode | null = useMemo(() => {
        if (!rawSelectedProjectElementNode) return null
        const {
            elementId,
            baseElementId,
            id,
            text,
            elementType,
            treePath,
            parentTreePath,
            parentNodeId,
        } = rawSelectedProjectElementNode
        return {
            elementId: elementId ?? undefined,
            baseElementId: baseElementId ?? undefined,
            id: id ?? undefined,
            text: text ?? undefined,
            elementType: elementType ?? undefined,
            treePath: treePath ?? undefined,
            parentTreePath: parentTreePath ?? undefined,
            parentNodeId: parentNodeId ?? undefined,
        }
    }, [rawSelectedProjectElementNode])

    const {
        isStudioManagement,
        activeTab,
        contentItemViewNodes,
        classificationNodes,
        expandedKeys,
        isTreeLoading,
        treeData,
        setTreeData,
        setExpandedKeys,
        setArticlesLoading,
    } = useShallowSelector((s) => ({
        isStudioManagement: s.isContentTeamFromStudioPage,
        activeTab: s.activeTab,
        contentItemViewNodes: s.contentItemViewNodes,
        classificationNodes: s.classificationNodes,
        expandedKeys: s.expandedKeys,
        isTreeLoading: s.isTreeLoading,
        treeData: s.treeData,
        setTreeData: s.actions.setTreeData,
        setExpandedKeys: s.actions.setExpandedKeys,
        setArticlesLoading: s.actions.setArticlesLoading,
    }))

    // Memoized computed values
    const memoContentItemViewIds = useMemo(
        () =>
            contentItemViewNodes
                ?.filter((x) => (x.textType as NodeType) === NODE_TYPES.Article)
                .map((x) => x.id ?? '') ?? [],
        [contentItemViewNodes]
    )

    const classificationNodeKeys = useMemo(
        () => classificationNodes?.map((node) => node.key),
        [classificationNodes]
    )

    // Custom hooks
    const permission = usePermissionChecks(treeData, selectedProjectElementNode)
    const { assemblyLevel, isDivisionLevel } = useAssemblyMetrics(
        selectedProjectElementNode
    )

    const { fetchContentItemView: fetchProductFamily } = useContentItemFetcher({
        projectId,
        treeData,
        setTreeData,
        permission,
    })

    const { withKeysToFetch, withKeysToCollapse } = useTreeDataMapping()
    const { setIsExportLoading } = useActionBarActions()
    const { exportSection } = useExportSectionMutation()
    const { getSupportingDocuments } = useSupportingDocumentsQuery()

    // Custom hook handlers
    const {
        handleExpandAll,
        handleCollapseAll,
        keysToExpand,
    } = useTreeExpansion(
        permission,
        memoContentItemViewIds,
        classificationNodeKeys,
        expandedKeys as string[],
        setArticlesLoading,
        fetchProductFamily,
        activeTab,
        treeData,
        withKeysToFetch,
        withKeysToCollapse
    )

    const {
        handleExportWord,
        handleExportPDF,
        handleViewPrintPreview,
    } = useExportHandlers(
        selectedProjectElementNode,
        isStudioManagement,
        projectId,
        projectProductFamilyId,
        setIsExportLoading,
        exportSection
    )

    const { handleViewSupportingDocs } = useSupportingDocs(
        projectProductFamilyId,
        getSupportingDocuments
    )

    const { handleOMVersionSelectChange } = useVersionManagement()

    // Computed values
    const expandCollapseDisabled = useMemo(
        () =>
            permission.isAssemblyTypeProject &&
            assemblyLevel === ASSEMBLY_CONFIG.firstLevel &&
            !isDivisionLevel
                ? false
                : isTreeLoading,
        [
            permission.isAssemblyTypeProject,
            assemblyLevel,
            isDivisionLevel,
            isTreeLoading,
        ]
    )

    return {
        handleViewPrintPreview,
        handleExportPDF,
        handleExportWord,
        handleViewSupportingDocs,
        handleExpandAll,
        handleCollapseAll,
        keysToExpand,
        isDivisionLevel,
        assemblyLevel,
        expandCollapseDisabled,
        handleOMVersionSelectChange,
    }
}
